import { initI18N } from "@similarweb/i18n-provider";
import Sentry from "@similarweb/sentry-logger";
// vite-plugin-react-click-to-component: Option+Right Click in the browser to open the source in your editor.
// Imported here (not via transformIndexHtml) because our custom dev-html-emitter bypasses that hook.
// In production the plugin's `load` hook returns an empty module for this id, so it tree-shakes to nothing —
// hence it stays a devDependency.
import "vite-plugin-react-click-to-component/client";

import type { SettingsResponse } from "app/@types/settings.types";
import type { StartupSettings } from "app/@types/startupSettings.types";
// Initialize Highcharts modules early to ensure hasData() and other module functions are available
import "app/libraries/reactHighcharts";

import { initCello } from "app/custom-hooks/cello/initCello";
import { siBootstrap } from "app/pages/sales-intelligence/bootstrap";
import { I18N_INIT_SENTRY_MESSAGE } from "app/sentry/constants";
import { initAppcues } from "app/services/AppcuesService";
import { initGrowthbook } from "app/services/trackingServices/GrowthBookService";
import { getHttpHeaders } from "app/services/fetchService/httpHeaders";
import { UPDATE_STARTUP_FORCE } from "app/UtilitiesAndConstants/Constants/queryParams";
import { customInterpolation } from "single-spa/localization/custom-interpolation";
import { swSettings } from "app/common/services/swSettings";
import { allMetrics } from "app/components/widget/widget-config/metrics/metricsRegistry";

import { initSharedServices } from "./bootstrap-service-init";
import { startBootstrapPhase, endBootstrapPhase } from "./bootstrap-timing";
import {
    withResponseBody,
    asyncReportWrapper,
    initWindowSettings,
    loadStartupSettings,
    reportFatalOnce,
    reportWrapper,
} from "./bootstrap-utils";
import { generateStore } from "./store/initialStore";

export const bootstrap = async () => {
    window.similarweb = window.similarweb ?? {};
    window.similarweb.config = window.similarweb.config ?? {};

    try {
        const {
            similarwebPromises: { startupSettingsPromise, allComponentsPromise } = {},
            componentToFetchOnBootstrap = [],
        } = window;
        if (!startupSettingsPromise) {
            throw new Error("startupSettings is not defined but is necessary for bootstrap");
        }

        // Fire-and-forget: start loading service chunks early. Safe because swSettings no longer
        // statically imports CountryService — the provider is wired explicitly below after initWindowSettings.
        reportWrapper(initSharedServices, "bootstrap-service-init failed to run");
        // Sales Intelligence owns its own data lifecycle. Fire-and-forget: sets
        // window.similarweb.userActivityData when resolved, does not block bootstrap.
        void siBootstrap();

        const refetchStartupSettings = (): Promise<Response> => {
            const shouldUpdateStartupForce = window.location.search.includes(UPDATE_STARTUP_FORCE);
            return fetch(`/api/startupSettings?force=${shouldUpdateStartupForce}`, {
                credentials: "include",
                method: "POST",
                headers: {
                    ...getHttpHeaders(),
                },
                body: JSON.stringify(window.componentToFetchOnBootstrap ?? []),
            });
        };

        const settingsPromise = asyncReportWrapper(
            loadStartupSettings<StartupSettings>(startupSettingsPromise, refetchStartupSettings),
            {
                errorDescription: "startupSettings failed to fetch",
            },
        );

        startBootstrapPhase("i18n-settings");
        // i18n and settings fetch in parallel, but both must resolve before initWindowSettings /
        // swSettings.initiate — otherwise eager translations (datepicker presets, country names,
        // widget metrics) run before i18n is ready and bake raw keys.
        const [i18n, settingsRes] = await Promise.all([
            initI18N({
                baseUrl: window.i18nURL,
                customInterpolation,
                onError: (error, extraParams) => {
                    Sentry.errorTracker({
                        message: `${I18N_INIT_SENTRY_MESSAGE} - ${extraParams.errorType}`,
                        e: error,
                        params: {
                            extra: extraParams,
                        },
                    });
                },
                projectIds: ["similarweb_pro", "similarweb_hooks", "similarweb_gaconnect"],
                version: window.i18nURL.includes("sandbox") ? "staging" : "production",
            }),
            settingsPromise,
        ]);
        endBootstrapPhase("i18n-settings");

        window.i18n = i18n;
        const startupSettings = settingsRes as StartupSettings;
        initWindowSettings(startupSettings);

        startBootstrapPhase("identities");
        const identitiesPromise = fetch(`/api/identities`, {
            headers: getHttpHeaders(),
        })
            .then((res) => {
                if (res.ok) {
                    return res.json();
                }
                const errorMessage = "Failed to fetch /api/identities from the server. Response is not OK.";
                Sentry.error(errorMessage, new Error(errorMessage));
                throw new Error(errorMessage);
            })
            .then((data) => {
                swSettings.subscriptionSolutions = data;
            })
            .catch((error) => {
                Sentry.error("Error fetching subscription solutions:", error);
                // Set empty array as fallback to prevent undefined errors downstream
                swSettings.subscriptionSolutions = [];
            });

        // Clean-up updateStartupForce parameter from URL
        // it's here to make sure it's cleaned on every app load
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has(UPDATE_STARTUP_FORCE)) {
            urlParams.delete(UPDATE_STARTUP_FORCE);
            const newUrl = new URL(window.location.href);
            newUrl.search = urlParams.toString();
            window.history.replaceState({}, "", newUrl);
        }

        const swSettingsInitListener = new Promise<boolean>((resolve) => {
            swSettings.onReady = () => resolve(true);
        });
        startBootstrapPhase("swSettings-ready");
        swSettings.initiate();
        await swSettingsInitListener;
        endBootstrapPhase("swSettings-ready");
        swSettings.updateComponentsStatus(componentToFetchOnBootstrap, startupSettings.settings);
        const clonedRes = { ...startupSettings };

        if (allComponentsPromise) {
            withResponseBody<SettingsResponse>(allComponentsPromise)
                .then((response = { components: {} }) => {
                    const { components = {} } = response;
                    const componentsArray = Object.keys(components);
                    swSettings.updateComponentsStatus(componentsArray, response);
                })
                .catch((err) => {
                    Sentry.errorTracker({
                        message: "allComponentsPromise failed, skipping full settings update",
                        e: err,
                        level: "error",
                    });
                });
        }
        swSettings.initPackageProperties(); // rerun it after loading configs. (hasSlAccess fails otherwise /sales/management not accesible.)
        swSettings.initWidgetMetrics(allMetrics);
        startBootstrapPhase("store");
        const store = reportWrapper(generateStore, "initStore failed to run");
        endBootstrapPhase("store");

        reportWrapper(() => initAppcues(swSettings));

        startBootstrapPhase("growthbook");
        const growthbookPromise = initGrowthbook(swSettings).finally(() => endBootstrapPhase("growthbook"));

        await Promise.all([identitiesPromise, growthbookPromise]);
        endBootstrapPhase("identities");

        initCello(swSettings);

        return {
            startup: {
                ...startupSettings,
            },
            clonedRes,
            i18n: window.i18n,
            store,
        };
    } catch (e) {
        reportFatalOnce(e, "Error in bootstrap");
        throw e;
    }
};
